import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Newspaper } from 'lucide-react';
import Sidebar from '../../components/sidebar/Sidebar';
import { useLogout } from '../../hooks/useLogout';
import { fetchNews, NewsArticle, NewsListResponse } from '../../services/api/newsService';
import {
  TaskyButton,
  TaskyEmptyState,
  TaskyErrorState,
  ResponsiveAppShell,
  TaskySpinner,
  TaskyTopBar,
} from '../../components/tasky';

const NewsListPage: React.FC = () => {
  const navigate = useNavigate();
  const handleLogout = useLogout();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [news, setNews] = useState<NewsArticle[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadNews = async (page: number) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetchNews(page);
      const articles = Array.isArray(response) ? response : (response.data || []);
      const totalLastPage = (response as NewsListResponse).last_page || 1;
      setNews(articles);
      setLastPage(totalLastPage);
    } catch (err) {
      console.error('Failed to fetch news:', err);
      setError('ニュースの読み込みに失敗しました。');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadNews(currentPage);
  }, [currentPage]);

  const handleArticleClick = (id: number) => {
    navigate(`/news/${id}`);
  };

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const sidebar = (
    <Sidebar
      onLogout={handleLogout}
      onToggleCollapse={setSidebarCollapsed}
      collapsed={sidebarCollapsed}
    />
  );
  const mobileSidebar = <Sidebar onLogout={handleLogout} />;

  return (
    <ResponsiveAppShell sidebar={sidebar} mobileSidebar={mobileSidebar} topbarTitle="ニュース">
      <div className="flex min-h-[calc(100dvh-56px)] flex-col bg-warm-bg lg:h-screen lg:min-h-0">
        {/* Header */}
        <TaskyTopBar title="ニュース" icon={<Newspaper className="w-5 h-5" />} />

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden bg-warm-bg">
          <div className="max-w-4xl mx-auto px-4 py-5 sm:px-6 sm:py-8">
            {isLoading && (
              <div className="flex flex-col items-center gap-2 py-12">
                <TaskySpinner size="md" />
                <span className="text-sm text-gray-500">読み込み中...</span>
              </div>
            )}

            {error && (
              <TaskyErrorState title="ニュースの読み込みに失敗しました" description={error} className="mb-6" />
            )}

            {!isLoading && !error && news.length === 0 && (
              <TaskyEmptyState
                icon={<Newspaper />}
                title="ニュースはまだありません。"
              />
            )}

            {!isLoading && news.length > 0 && (
              <div className="flex flex-col gap-4" data-tutorial="news-list">
                {news.map((article) => (
                  <button
                    key={article.id}
                    type="button"
                    onClick={() => handleArticleClick(article.id)}
                    className="flex flex-col bg-white border border-warm-border rounded-xl overflow-hidden hover:shadow-card hover:border-primary/30 transition text-left sm:flex-row"
                  >
                    {article.thumbnail_url ? (
                      <div className="h-40 w-full bg-warm-surface overflow-hidden sm:h-[120px] sm:w-[180px] sm:min-w-[180px]">
                        <img
                          src={article.thumbnail_url}
                          alt={article.title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ) : (
                      <div className="h-32 w-full bg-warm-surface flex items-center justify-center text-gray-300 text-3xl sm:h-[120px] sm:w-[180px] sm:min-w-[180px]">
                        📰
                      </div>
                    )}
                    <div className="px-4 py-4 flex-1 flex flex-col justify-center sm:px-6 sm:py-5">
                      <span className="text-xs text-gray-400 mb-1.5">
                        {formatDate(article.published_at || article.created_at)}
                      </span>
                      <h2 className="text-base font-semibold text-dark leading-relaxed line-clamp-2 m-0">
                        {article.title}
                      </h2>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* Pagination */}
            {lastPage > 1 && (
              <div className="flex justify-center items-center gap-2 mt-10">
                <TaskyButton
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                >
                  前へ
                </TaskyButton>
                <span className="px-4 py-2 text-gray-500 text-sm">
                  {currentPage} / {lastPage}
                </span>
                <TaskyButton
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((prev) => Math.min(lastPage, prev + 1))}
                  disabled={currentPage === lastPage}
                >
                  次へ
                </TaskyButton>
              </div>
            )}
          </div>
        </div>
      </div>
    </ResponsiveAppShell>
  );
};

export default NewsListPage;
